import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  CircleDollarSign,
  TrendingUp,
  Users,
  BookOpen,
  GaugeCircle,
  Plus,
  BarChart3,
  User,
  ArrowRight,
  Loader2,
  Clock3,
  Inbox,
  AlertTriangle,
  FilePlus,
  ShieldCheck,
  Pencil,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { useGetInstructorAnalyticsQuery } from '../../api/apiSlice';
import { computePricing, formatKzt } from '../../lib/coursePricing';

// InstructorDashboard
// ───────────────────
// Главная страница инструктора. Раньше был просто <h1>панель инструктора</h1>.
// Теперь — реальная сводка: KPI, быстрые действия, топ курсов, последние
// покупки, статус публикаций.
//
// Источник данных — единый эндпоинт /api/instructor/analytics?days=30,
// который мы уже используем для странички Аналитики. Один запрос, без N+1.

const fmtNum = (n) => Number(n || 0).toLocaleString('ru-RU');

const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString('ru-RU', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

// Приветствие зависит от часа — мелочь, но добавляет тепла.
function pickGreeting(t) {
  const h = new Date().getHours();
  if (h < 12) return t('instructorDashboard.greetingMorning');
  if (h < 18) return t('instructorDashboard.greetingDay');
  return t('instructorDashboard.greetingEvening');
}

const InstructorDashboard = () => {
  const { t } = useTranslation();
  const { user } = useSelector((s) => s.auth);

  // Период 30 дней — стандартный для дашборда. Если нужны другие — отдельная
  // страница «Аналитика» (там переключатель 7/30/90/365).
  const { data, isLoading, error } = useGetInstructorAnalyticsQuery({ days: 30 });

  const summary = data?.summary || {};
  const performance = data?.coursePerformance || [];
  const recents = data?.recentEnrollments || [];

  // Топ-3 курса по доходу. coursePerformance уже отсортирован на бэке.
  const topCourses = useMemo(() => performance.slice(0, 3), [performance]);

  // Средний % завершения по всем курсам — невзвешенный, но хватит для KPI.
  const avgCompletion = useMemo(() => {
    if (!performance.length) return 0;
    const withStudents = performance.filter((c) => c.students > 0);
    if (!withStudents.length) return 0;
    const sum = withStudents.reduce((s, c) => s + (c.completionRate || 0), 0);
    return Math.round(sum / withStudents.length);
  }, [performance]);

  // Статус-счётчики (для секции Status breakdown).
  const statusCounts = useMemo(() => {
    const c = { draft: 0, pending_review: 0, active: 0, rejected: 0 };
    for (const co of performance) {
      const key = co.status === 'inactive' ? 'rejected' : co.status;
      if (c[key] !== undefined) c[key] += 1;
    }
    return c;
  }, [performance]);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <SkeletonDashboard />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 dark:bg-red-950/20 p-5 text-red-700 dark:text-red-300 max-w-2xl m-6">
        <AlertTriangle className="w-5 h-5 inline mr-1" />
        {error?.data?.message || t('errors.generic')}
      </div>
    );
  }

  const greeting = pickGreeting(t);
  const firstName = (user?.name || '').split(' ')[0] || user?.email?.split('@')[0] || '';

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Header / greeting */}
      <header className="rounded-3xl border border-gray-200 dark:border-gray-700 bg-gradient-to-br from-indigo-600 via-purple-600 to-fuchsia-600 text-white p-6 sm:p-8 shadow-sm relative overflow-hidden">
        {/* Soft decorative blob — современный «техно» вайб. */}
        <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full bg-white/10 blur-2xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-10 h-40 w-40 rounded-full bg-white/10 blur-2xl pointer-events-none" />

        <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-white/80">{greeting}{firstName ? `,` : ''}</p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              {firstName || t('roles.instructor')} 👋
            </h1>
            <p className="mt-2 text-sm text-white/80 max-w-xl">
              {t('instructorDashboard.subtitle')}
            </p>
          </div>

          <Link
            to="/dashboard/instructor/create-course"
            className="inline-flex items-center gap-2 rounded-2xl bg-white text-indigo-700 hover:bg-indigo-50 transition px-5 py-3 text-sm font-bold shadow-lg shrink-0"
          >
            <Plus className="w-4 h-4" />
            {t('instructorDashboard.qaCreate')}
          </Link>
        </div>
      </header>

      {/* KPI grid */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={CircleDollarSign}
          label={t('instructorDashboard.kpiRevenueMonth')}
          value={formatKzt(summary.periodRevenue, { free: '0 ₸' })}
          sub={t('instructorDashboard.ordersMonth', { count: summary.periodOrders || 0 })}
          color="emerald"
        />
        <KpiCard
          icon={TrendingUp}
          label={t('instructorDashboard.kpiRevenueTotal')}
          value={formatKzt(summary.totalRevenue, { free: '0 ₸' })}
          color="indigo"
        />
        <KpiCard
          icon={Users}
          label={t('instructorDashboard.kpiStudents')}
          value={fmtNum(summary.totalStudents)}
          color="blue"
        />
        <KpiCard
          icon={GaugeCircle}
          label={t('instructorDashboard.kpiCompletion')}
          value={`${avgCompletion}%`}
          sub={t('instructorDashboard.activeCourses', { count: summary.activeCourses || 0 })}
          color="purple"
        />
      </section>

      {/* Quick actions */}
      <section>
        <h2 className="text-base font-bold text-gray-900 dark:text-white mb-3">
          {t('instructorDashboard.quickActions')}
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <QuickAction
            to="/dashboard/instructor/create-course"
            icon={FilePlus}
            label={t('instructorDashboard.qaCreate')}
            sub={t('instructorDashboard.qaCreateSub')}
            color="indigo"
          />
          <QuickAction
            to="/dashboard/instructor/my-courses"
            icon={BookOpen}
            label={t('instructorDashboard.qaCourses')}
            sub={t('instructorDashboard.qaCoursesSub')}
            color="blue"
          />
          <QuickAction
            to="/dashboard/instructor/analytics"
            icon={BarChart3}
            label={t('instructorDashboard.qaAnalytics')}
            sub={t('instructorDashboard.qaAnalyticsSub')}
            color="purple"
          />
          <QuickAction
            to="/profile"
            icon={User}
            label={t('instructorDashboard.qaProfile')}
            sub={t('instructorDashboard.qaProfileSub')}
            color="emerald"
          />
        </div>
      </section>

      {/* Top courses + Recent sales */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Top courses (3/5 width) */}
        <section className="lg:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-gray-900 dark:text-white">
              {t('instructorDashboard.topCourses')}
            </h2>
            {performance.length > 3 && (
              <Link
                to="/dashboard/instructor/my-courses"
                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                {t('instructorDashboard.viewAll')} <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </div>

          {topCourses.length === 0 ? (
            <EmptyState
              icon={Inbox}
              text={t('instructorDashboard.topCoursesEmpty')}
              ctaTo="/dashboard/instructor/create-course"
              ctaLabel={t('instructorDashboard.topCoursesEmptyCta')}
            />
          ) : (
            <div className="space-y-3">
              {topCourses.map((c) => (
                <TopCourseCard key={c._id} course={c} t={t} />
              ))}
            </div>
          )}
        </section>

        {/* Recent sales (2/5 width) */}
        <section className="lg:col-span-2">
          <h2 className="text-base font-bold text-gray-900 dark:text-white mb-3">
            {t('instructorDashboard.recentSales')}
          </h2>
          {recents.length === 0 ? (
            <EmptyState icon={Users} text={t('instructorDashboard.recentEmpty')} />
          ) : (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
              {recents.slice(0, 6).map((r) => (
                <div key={r._id} className="flex items-center gap-3 p-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {(r.user?.name || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                      {r.user?.name || '—'}
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                      {t('instructorDashboard.boughtVerb')} «{r.course?.title || '—'}»
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                      {formatKzt(r.amount, { free: '0 ₸' })}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 inline-flex items-center gap-0.5">
                      <Clock3 className="w-2.5 h-2.5" />
                      {fmtDateTime(r.paidAt || r.createdAt)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Status breakdown */}
      <section>
        <h2 className="text-base font-bold text-gray-900 dark:text-white mb-3">
          {t('instructorDashboard.statusBreakdown')}
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatusCard
            icon={Pencil}
            label={t('instructorDashboard.statusDraft')}
            count={statusCounts.draft}
            color="gray"
            to="/dashboard/instructor/my-courses"
          />
          <StatusCard
            icon={ShieldCheck}
            label={t('instructorDashboard.statusPending')}
            count={statusCounts.pending_review}
            color="amber"
            to="/dashboard/instructor/my-courses"
          />
          <StatusCard
            icon={CheckCircle2}
            label={t('instructorDashboard.statusActive')}
            count={statusCounts.active}
            color="emerald"
            to="/dashboard/instructor/my-courses"
          />
          <StatusCard
            icon={XCircle}
            label={t('instructorDashboard.statusRejected')}
            count={statusCounts.rejected}
            color="rose"
            to="/dashboard/instructor/my-courses"
          />
        </div>
      </section>
    </div>
  );
};

// ─── pieces ─────────────────────────────────────────────────────────
const KPI_PALETTE = {
  indigo: 'bg-indigo-100 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300',
  blue: 'bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300',
  emerald: 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  purple: 'bg-purple-100 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300',
};

const KpiCard = ({ icon: Icon, label, value, sub, color = 'indigo' }) => (
  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 transition hover:shadow-md">
    <div className="flex items-start justify-between mb-2">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      <span className={`p-1.5 rounded-lg ${KPI_PALETTE[color]}`}>
        <Icon className="w-3.5 h-3.5" />
      </span>
    </div>
    <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tabular-nums truncate">
      {value}
    </div>
    {sub && (
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
        {sub}
      </div>
    )}
  </div>
);

const QUICK_PALETTE = {
  indigo: 'from-indigo-500 to-indigo-600 group-hover:from-indigo-600 group-hover:to-indigo-700',
  blue: 'from-blue-500 to-blue-600 group-hover:from-blue-600 group-hover:to-blue-700',
  purple: 'from-purple-500 to-purple-600 group-hover:from-purple-600 group-hover:to-purple-700',
  emerald: 'from-emerald-500 to-emerald-600 group-hover:from-emerald-600 group-hover:to-emerald-700',
};

const QuickAction = ({ to, icon: Icon, label, sub, color }) => (
  <Link
    to={to}
    className="group rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 hover:shadow-lg transition flex items-center gap-3"
  >
    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white transition ${QUICK_PALETTE[color]}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{label}</div>
      <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{sub}</div>
    </div>
    <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-indigo-500 shrink-0" />
  </Link>
);

const TopCourseCard = ({ course, t }) => {
  const { hasDiscount, basePrice, finalPrice, discountPercent } = computePricing(course);
  return (
    <Link
      to={`/dashboard/instructor/create-course/${course._id}/edit`}
      className="group block rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 sm:p-4 hover:shadow-md transition"
    >
      <div className="flex items-center gap-3 sm:gap-4">
        {/* R2 cost-save: НЕ грузим coverImageUrl в analytics-карточке.
            Top-courses список открывается на каждой загрузке дашборда — это
            постоянный фоновый расход R2-reads. Буквенный thumbnail вместо. */}
        <div className="relative h-16 w-16 sm:h-20 sm:w-20 rounded-xl flex items-center justify-center bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 text-xl sm:text-2xl font-bold shrink-0">
          {(course.title || '?').trim().charAt(0).toUpperCase()}
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white truncate">
            {course.title}
          </h3>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
              <Users className="w-3 h-3" />
              {course.students} {t('instructorDashboard.studentsLabel')}
            </span>
            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold tabular-nums">
              {formatKzt(course.revenue, { free: '0 ₸' })}
            </span>
            {hasDiscount && (
              <span className="text-[10px] rounded-full bg-rose-100 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 px-1.5 py-0.5 font-semibold">
                −{discountPercent}%
              </span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-2">
            {/* Прогресс-бар = доля этого курса в твоём общем доходе за период
                (revenueShare). Раньше показывали completionRate (студенты
                закончили курс) — но 99% юзеров ещё в процессе, бар всегда был 0%.
                В контексте "Топ по доходу" логичнее показывать share, чтобы
                было видно: курс A — 60% всего дохода, курс B — 30%, курс C — 10%. */}
            <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
                style={{ width: `${course.revenueShare || 0}%` }}
              />
            </div>
            <span className="text-[10px] tabular-nums text-gray-500 dark:text-gray-400">
              {course.revenueShare || 0}%
            </span>
          </div>
        </div>

        <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-indigo-500 shrink-0 hidden sm:block" />
      </div>
    </Link>
  );
};

const STATUS_PALETTE = {
  gray: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200',
  amber: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300',
  emerald: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  rose: 'bg-rose-100 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300',
};

const StatusCard = ({ icon: Icon, label, count, color, to }) => (
  <Link
    to={to}
    className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 hover:shadow-md transition flex items-center justify-between gap-3"
  >
    <div className="min-w-0">
      <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${STATUS_PALETTE[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{count}</div>
    </div>
  </Link>
);

const EmptyState = ({ icon: Icon, text, ctaTo, ctaLabel }) => (
  <div className="rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-700 p-8 text-center text-gray-500 dark:text-gray-400">
    <Icon className="w-10 h-10 mx-auto mb-2 text-gray-400" />
    <p className="text-sm">{text}</p>
    {ctaTo && (
      <Link
        to={ctaTo}
        className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 text-sm font-semibold"
      >
        <Plus className="w-4 h-4" /> {ctaLabel}
      </Link>
    )}
  </div>
);

const SkeletonDashboard = () => (
  <div className="space-y-6 animate-pulse">
    <div className="h-32 rounded-3xl bg-gray-200 dark:bg-gray-800" />
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-24 rounded-2xl bg-gray-200 dark:bg-gray-800" />
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-24 rounded-2xl bg-gray-200 dark:bg-gray-800" />
        ))}
      </div>
      <div className="lg:col-span-2 h-72 rounded-2xl bg-gray-200 dark:bg-gray-800" />
    </div>
  </div>
);

export default InstructorDashboard;
